import type { IChatAttachmentBrief, ICitation } from "@/api";
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuSub,
    DropdownMenuSubContent,
    DropdownMenuSubTrigger,
    DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
    Tooltip,
    TooltipContent,
    TooltipTrigger,
} from "@/components/ui/tooltip";
import { cn } from "@/lib/utils";
import { TriangleAlertIcon, XIcon } from "lucide-react";
import type { FC } from "react";
import type { AttachmentAction } from "./attachment-action";
import {
    MAX_VISIBLE,
    buildReferences,
    getMenuOptions,
    getReferenceStyle,
    type ReferenceItem,
} from "./attachment-pill-row.utils";

interface AttachmentPillRowProps {
    citations: ICitation[];
    attachments: IChatAttachmentBrief[];
    onAction?: (ref: ReferenceItem, action: AttachmentAction) => void;
    onRemove?: (ref: ReferenceItem) => void;
    maxVisible?: number;
    className?: string;
    /**
     * Optional per-pill advisory (e.g. the composer's cross-client scope
     * warning). A returned string renders a non-blocking inline indicator
     * on that pill; the pill itself stays fully functional.
     */
    warningFor?: (ref: ReferenceItem) => string | undefined;
}

// ---------------------------------------------------------------------------
// Sub-components
// ---------------------------------------------------------------------------

const TypeIcon: FC<{ reference: ReferenceItem }> = ({ reference }) => {
    const style = getReferenceStyle(reference);
    const Icon = style.icon;
    return (
        <span
            className="flex size-7 shrink-0 items-center justify-center rounded-md"
            style={{ backgroundColor: style.bg, color: style.color }}
        >
            <Icon className="size-3.5" />
        </span>
    );
};

const ActionMenuItems: FC<{
    ref: ReferenceItem;
    onAction: NonNullable<AttachmentPillRowProps["onAction"]>;
}> = ({ ref, onAction }) => (
    <>
        {getMenuOptions(ref).map((opt) => (
            <DropdownMenuItem
                key={opt.action}
                className="gap-2 px-2 py-1.5 text-[13px]"
                onClick={() => onAction(ref, opt.action)}
            >
                <span className="flex size-4 shrink-0 items-center justify-center">
                    {opt.icon}
                </span>
                {opt.label}
            </DropdownMenuItem>
        ))}
    </>
);

/**
 * Inline, non-blocking advisory on a pill (icon + tooltip). Rendered as a
 * span so it nests safely inside both pill variants (the action-mode pill
 * is itself a button).
 */
const PillWarning: FC<{ text: string }> = ({ text }) => (
    <Tooltip>
        <TooltipTrigger
            render={
                <span
                    data-testid="attachment-scope-warning"
                    className="flex shrink-0 items-center text-amber-600"
                />
            }
        >
            <TriangleAlertIcon aria-hidden="true" className="size-3" />
            <span className="sr-only">{text}</span>
        </TooltipTrigger>
        <TooltipContent side="top">{text}</TooltipContent>
    </Tooltip>
);

const PillWithMenu: FC<{
    item: ReferenceItem;
    onAction: NonNullable<AttachmentPillRowProps["onAction"]>;
    warning?: string;
}> = ({ item, onAction, warning }) => {
    const style = getReferenceStyle(item);
    const Icon = style.icon;
    return (
        <DropdownMenu>
            <DropdownMenuTrigger
                className={cn(
                    "inline-flex max-w-40 shrink-0 cursor-pointer items-center gap-1.5 rounded-lg px-2 py-1",
                    "text-xs font-semibold transition-colors hover:underline"
                )}
                style={{ backgroundColor: style.bg, color: style.color }}
            >
                <Icon className="size-3 shrink-0" />
                <span className="truncate">{item.name}</span>
                {warning ? <PillWarning text={warning} /> : null}
            </DropdownMenuTrigger>
            <DropdownMenuContent align="start" className="min-w-48">
                <ActionMenuItems ref={item} onAction={onAction} />
            </DropdownMenuContent>
        </DropdownMenu>
    );
};

const PillWithRemove: FC<{
    item: ReferenceItem;
    onRemove: NonNullable<AttachmentPillRowProps["onRemove"]>;
    warning?: string;
}> = ({ item, onRemove, warning }) => {
    const style = getReferenceStyle(item);
    const Icon = style.icon;
    return (
        <span
            className="inline-flex max-w-40 shrink-0 items-center gap-1.5 rounded-lg py-1 pr-1 pl-2 text-xs font-semibold"
            style={{ backgroundColor: style.bg, color: style.color }}
        >
            <Icon className="size-3 shrink-0" />
            <span className="truncate">{item.name}</span>
            {warning ? <PillWarning text={warning} /> : null}
            <button
                type="button"
                onClick={() => onRemove(item)}
                aria-label={`Remove ${item.name}`}
                className="rounded-sm p-0.5 opacity-60 transition-opacity hover:opacity-100"
            >
                <XIcon className="size-3" />
            </button>
        </span>
    );
};

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------

/**
 * Shared pill row with two modes: `onAction` for action-menu pills,
 * `onRemove` for removable pills. Pills exceeding `maxVisible` collapse
 * into a "+N" dropdown.
 */
export const AttachmentPillRow: FC<AttachmentPillRowProps> = ({
    citations,
    attachments,
    onAction,
    onRemove,
    maxVisible = MAX_VISIBLE,
    className,
    warningFor,
}) => {
    const references = buildReferences(citations, attachments);
    if (references.length === 0) return null;

    const visibleRefs = references.slice(0, maxVisible);
    const overflowRefs = references.slice(maxVisible);

    return (
        <div
            className={cn(
                "flex min-w-0 flex-nowrap items-center gap-1.5",
                className
            )}
        >
            {visibleRefs.map((ref) =>
                onRemove ? (
                    <PillWithRemove
                        key={`${ref.type}-${ref.id}`}
                        item={ref}
                        onRemove={onRemove}
                        warning={warningFor?.(ref)}
                    />
                ) : onAction ? (
                    <PillWithMenu
                        key={`${ref.type}-${ref.id}`}
                        item={ref}
                        onAction={onAction}
                        warning={warningFor?.(ref)}
                    />
                ) : null
            )}

            {overflowRefs.length > 0 && (
                <DropdownMenu>
                    <DropdownMenuTrigger
                        className={cn(
                            "bg-muted text-muted-foreground hover:bg-muted/80",
                            "inline-flex shrink-0 cursor-pointer items-center rounded-lg px-2.5 py-1",
                            "text-xs font-semibold transition-colors"
                        )}
                    >
                        +{overflowRefs.length}
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="start" className="min-w-56">
                        {overflowRefs.map((ref) =>
                            onRemove ? (
                                <DropdownMenuItem
                                    key={`${ref.type}-${ref.id}`}
                                    className="gap-2.5 px-2 py-1.5 text-[13px]"
                                    onSelect={(e) => {
                                        e.preventDefault();
                                        onRemove(ref);
                                    }}
                                >
                                    <TypeIcon reference={ref} />
                                    <span className="flex-1 truncate font-medium">
                                        {ref.name}
                                    </span>
                                    {warningFor?.(ref) ? (
                                        // No nested tooltip inside an open
                                        // menu; the icon + sr-only text carry
                                        // the advisory here.
                                        <span
                                            data-testid="attachment-scope-warning"
                                            className="flex shrink-0 items-center text-amber-600"
                                        >
                                            <TriangleAlertIcon
                                                aria-hidden="true"
                                                className="size-3.5"
                                            />
                                            <span className="sr-only">
                                                {warningFor(ref)}
                                            </span>
                                        </span>
                                    ) : null}
                                    <XIcon className="text-muted-foreground size-3.5" />
                                </DropdownMenuItem>
                            ) : onAction ? (
                                <DropdownMenuSub key={`${ref.type}-${ref.id}`}>
                                    <DropdownMenuSubTrigger className="gap-2.5 px-2 py-1.5 text-[13px]">
                                        <TypeIcon reference={ref} />
                                        <span className="truncate font-medium">
                                            {ref.name}
                                        </span>
                                    </DropdownMenuSubTrigger>
                                    <DropdownMenuSubContent className="min-w-48">
                                        <ActionMenuItems
                                            ref={ref}
                                            onAction={onAction}
                                        />
                                    </DropdownMenuSubContent>
                                </DropdownMenuSub>
                            ) : null
                        )}
                    </DropdownMenuContent>
                </DropdownMenu>
            )}
        </div>
    );
};
